import * as stylex from '@stylexjs/stylex'
import { useState } from 'react'

import Dialog from './Dialog'
import Icon from './Icon'
import type { CreatePersonInput } from '../lib/ledger'


const styles = stylex.create({
  s0: {
    display: "flex",
    flexDirection: "column",
    rowGap: "20px",
  },
  s1: {
    display: "block",
  },
  s2: {
    marginBottom: "8px",
    display: "block",
    fontSize: "13px",
    fontWeight: 600,
    color: "#3d463f",
  },
  s3: {
    height: "48px",
    width: "100%",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
    paddingInline: "14px",
    fontSize: "14px",
    outline: "none",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    "::placeholder": {
      color: "#99a099",
    },
    ":focus": {
      borderColor: "#5b9a6c",
      boxShadow: "0 0 0 1px color-mix(in srgb, #5b9a6c 10%, transparent)",
    },
  },
  s4: {
    display: "grid",
    gap: "16px",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  s5: {
    fontWeight: 400,
    color: "#869087",
  },
  s6: {
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#f0d0c0",
    backgroundColor: "#fff5f0",
    paddingInline: "14px",
    paddingBlock: "12px",
    fontSize: "13px",
    color: "#9b491e",
  },
  s7: {
    display: "flex",
    flexDirection: "column-reverse",
    gap: "12px",
    borderTopWidth: 1,
    borderColor: "#e8ece7",
    paddingTop: "20px",
    "@media (min-width: 640px)": {
      flexDirection: "row",
      justifyContent: "end",
    },
  },
  s8: {
    height: "44px",
    borderRadius: "0.75rem",
    paddingInline: "16px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#607068",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#f0f4ef",
    },
  },
  s9: {
    display: "flex",
    height: "44px",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    backgroundColor: "#408b56",
    paddingInline: "20px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#fff",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#347c48",
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.6,
    },
  },
  s10: {
    height: "16px",
    width: "16px",
  },
})
type PersonDialogProps = {
  onClose: () => void
  onSave: (input: CreatePersonInput) => Promise<void>
}

export default function PersonDialog({ onClose, onSave }: PersonDialogProps) {
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const name = displayName.trim()
    if (!name) {
      setError('Add a name so you can recognize this person later.')
      return
    }
    setSaving(true)
    setError('')
    try {
      await onSave({ displayName: name, email: email.trim(), phone: phone.trim() })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'We could not add that person.')
      setSaving(false)
    }
  }

  return (
    <Dialog description="Add someone to your circle before you split a bill together." onClose={saving ? () => undefined : onClose} title="Add a person">
      <form {...stylex.props(styles.s0)} onSubmit={submit}>
        <label {...stylex.props(styles.s1)}><span {...stylex.props(styles.s2)}>Name</span><input autoFocus {...stylex.props(styles.s3)} data-dialog-autofocus maxLength={100} onChange={(event) => setDisplayName(event.target.value)} placeholder="Patrick Everon" value={displayName} /></label>
        <div {...stylex.props(styles.s4)}>
          <label {...stylex.props(styles.s1)}><span {...stylex.props(styles.s2)}>Email <span {...stylex.props(styles.s5)}>(optional)</span></span><input {...stylex.props(styles.s3)} inputMode="email" onChange={(event) => setEmail(event.target.value)} placeholder="patrick@example.com" type="email" value={email} /></label>
          <label {...stylex.props(styles.s1)}><span {...stylex.props(styles.s2)}>Phone <span {...stylex.props(styles.s5)}>(optional)</span></span><input {...stylex.props(styles.s3)} inputMode="tel" onChange={(event) => setPhone(event.target.value)} placeholder="+297 …" type="tel" value={phone} /></label>
        </div>
        {error && <p {...stylex.props(styles.s6)} role="alert">{error}</p>}
        <div {...stylex.props(styles.s7)}><button {...stylex.props(styles.s8)} disabled={saving} onClick={onClose} type="button">Cancel</button><button {...stylex.props(styles.s9)} disabled={!displayName.trim() || saving} type="submit">{saving ? 'Adding…' : 'Add person'}<Icon {...stylex.props(styles.s10)} name="arrow-right" /></button></div>
      </form>
    </Dialog>
  )
}
